<template>
  <div class="password-settings">
    <h2>修改登录密码</h2>
    <p class="password-hint">验证码将发送到当前账号绑定的手机号。</p>
    <div class="password-requirements">
      <strong>密码要求</strong>
      <p>
        密码长度需为 8 至 64
        个字符，不能包含空白字符，建议包含数字、字母和符号。
      </p>
    </div>
    <el-form
      hide-required-asterisk
      ref="passwordForm"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item label="短信验证码" prop="smsCode" for="password-smsCode">
        <div class="password-code-row">
          <el-input
            id="password-smsCode"
            v-model.trim="form.smsCode"
            maxlength="6"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="请输入验证码"
            class="password-input"
          />
          <el-button
            :disabled="codeCountdown > 0 || sendingCode"
            :loading="sendingCode"
            type="primary"
            @click="handleSendCode"
          >
            {{ codeCountdown > 0 ? `${codeCountdown}s 后重试` : "获取验证码" }}
          </el-button>
        </div>
      </el-form-item>
      <el-form-item
        label="新密码"
        prop="newPassword"
        for="password-newPassword"
      >
        <el-input
          id="password-newPassword"
          placeholder="请输入新密码"
          v-model="form.newPassword"
          type="password"
          maxlength="64"
          autocomplete="new-password"
          class="password-input"
        />
      </el-form-item>
      <el-form-item
        label="确认新密码"
        prop="confirmPassword"
        for="password-confirmPassword"
      >
        <el-input
          id="password-confirmPassword"
          placeholder="请再次输入新密码"
          v-model="form.confirmPassword"
          type="password"
          maxlength="64"
          autocomplete="new-password"
          class="password-input"
          @keyup.enter.native="handleSubmit"
        />
      </el-form-item>
      <el-form-item>
        <el-button :disabled="submitting" @click="cancelEdit">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit"
          >确认修改</el-button
        >
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { isValidPassword, PASSWORD_VALIDATION_MESSAGE } from "@/tool/password";

const validateSmsCode = (rule, value, callback) => {
  if (!/^\d{6}$/.test(value || "")) {
    callback(new Error("请输入验证码"));
    return;
  }
  callback();
};

export default {
  data() {
    const validateNewPassword = (rule, value, callback) => {
      if (!isValidPassword(value || "")) {
        callback(new Error(PASSWORD_VALIDATION_MESSAGE));
        return;
      }
      if (this.form.confirmPassword) {
        this.$refs.passwordForm.validateField("confirmPassword");
      }
      callback();
    };
    const validateConfirmation = (rule, value, callback) => {
      if (value !== this.form.newPassword) {
        callback(new Error("两次输入的新密码不一致"));
        return;
      }
      callback();
    };

    return {
      form: {
        smsCode: "",
        newPassword: "",
        confirmPassword: "",
      },
      rules: {
        smsCode: [
          { required: true, validator: validateSmsCode, trigger: "blur" },
        ],
        newPassword: [
          { required: true, validator: validateNewPassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, validator: validateConfirmation, trigger: "blur" },
        ],
      },
      codeCountdown: 0,
      codeTimer: null,
      sendingCode: false,
      requestEpoch: 0,
      submitting: false,
    };
  },
  methods: {
    ...mapActions(["sendChangePasswordCode", "handleChangePwd"]),
    captureIdentity() {
      const version = this.$store.state.user.sessionVersion;
      const epoch = this.requestEpoch;
      return () =>
        version === this.$store.state.user.sessionVersion &&
        epoch === this.requestEpoch;
    },
    cancelEdit() {
      this.form = { smsCode: "", newPassword: "", confirmPassword: "" };
      this.$refs.passwordForm.clearValidate();
      this.$router.push("/center/basic");
    },
    startCountdown() {
      this.codeCountdown = 60;
      clearInterval(this.codeTimer);
      this.codeTimer = setInterval(() => {
        this.codeCountdown -= 1;
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
        }
      }, 1000);
    },
    handleSendCode() {
      if (this.sendingCode || this.codeCountdown > 0) return;
      const isCurrent = this.captureIdentity();
      this.sendingCode = true;
      this.sendChangePasswordCode()
        .then(({ message, smsCode }) => {
          if (!isCurrent()) return;
          this.$message.success(message);
          if (smsCode) {
            this.form.smsCode = smsCode;
          }
          this.startCountdown();
        })
        .catch((message) => {
          if (isCurrent()) this.$message.error(message);
        })
        .finally(() => {
          this.sendingCode = false;
        });
    },
    handleSubmit() {
      if (this.submitting) return;
      const isCurrent = this.captureIdentity();
      this.$refs.passwordForm.validate((valid) => {
        if (!valid || !isCurrent()) return;
        this.submitting = true;
        this.handleChangePwd({
          smsCode: this.form.smsCode,
          newPassword: this.form.newPassword,
        })
          .then((result) => {
            if (!isCurrent()) return;
            if (result.type !== "success") {
              this.$message.error(result.msg);
              return;
            }
            this.form = { smsCode: "", newPassword: "", confirmPassword: "" };
            this.$message.success(result.msg);
          })
          .catch(() => {
            if (isCurrent()) this.$message.error("修改失败，请稍后重试");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
  },
  beforeDestroy() {
    this.requestEpoch += 1;
    clearInterval(this.codeTimer);
  },
};
</script>

<style scoped>
.password-settings {
  padding: 32px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 8px 16px #24213a08;
  color: #24242a;
}
.password-settings h2 {
  margin: 0 0 8px;
  font-size: 24px;
}
.password-hint {
  color: #777780;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 16px;
}
.password-requirements {
  padding: 16px;
  background: #f0edfd;
  border-radius: 16px;
  margin-bottom: 16px;
}
.password-requirements strong {
  font-size: 14px;
  color: #6b5dd5;
}
.password-requirements p {
  font-size: 13px;
  color: #777780;
  line-height: 1.6;
  margin: 10px 0 0;
}
.password-settings >>> .el-form-item {
  max-width: 570px;
  margin-bottom: 20px;
}
.password-settings >>> .el-form-item__label {
  font-size: 14px;
  line-height: 21px;
  padding-bottom: 9px;
  color: #777780;
}
.password-settings >>> .el-input__inner {
  height: 44px;
  border-radius: 12px;
  background: #fafafc;
  border-color: #e9e9ef;
}
.password-settings >>> .el-input__inner:focus {
  border-color: #7c6ff0;
}
.password-input {
  width: 100%;
}
.password-code-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.password-code-row .password-input {
  flex: 1;
  min-width: 0;
}
.password-settings .el-button {
  height: 40px;
  padding: 0 24px;
  border-radius: 100px;
}
.password-settings .el-button--primary {
  background: #7c6ff0;
  border-color: #7c6ff0;
}
.password-code-row .el-button {
  padding: 0 18px;
}
.password-settings >>> .el-form-item:last-child {
  margin-bottom: 0;
}
.password-settings button:focus-visible {
  outline: 2px solid #6b5dd5;
  outline-offset: 3px;
}
@media (max-width: 700px) {
  .password-settings {
    padding: 24px 20px;
    border-radius: 20px;
  }
  .password-settings h2 {
    font-size: 22px;
  }
  .password-code-row .el-button {
    padding: 0 12px;
  }
  .password-settings >>> .el-form-item {
    max-width: 100%;
  }
}
</style>
<style scoped>
.password-settings h2 {
  font-weight: 700;
}
.password-settings >>> .el-form-item__label {
  font-weight: 400;
}
</style>
